import React, { useEffect, useRef, useState } from 'react'
import gallery from './static/gallery.json';
import { loadNocoCollections, applyLanguage } from './noco';

const Context = React.createContext();


function Provider({ children }) {

  // Helper function to retry requests with exponential backoff for rate limiting
  const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error) {
        const isRateLimit =
          error.statusCode === 429 ||
          error.status === 429 ||
          (error.error && (error.error.type === 'RATE_LIMIT_EXCEEDED' || error.error.statusCode === 429)) ||
          (error.message && error.message.includes('429')) ||
          (error.message && error.message.includes('Too Many Requests'));
          
        if (isRateLimit && attempt < maxRetries - 1) {
          const delay = baseDelay * Math.pow(2, attempt);
          console.warn(`Rate limited (429). Retrying in ${delay}ms... (attempt ${attempt + 1}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        throw error;
      }
    }
  };


  const [ready, setReady] = useState(false)
  const [language, setLanguage] = useState('en')

  const [view, setView] = useState('g')

  const [search, setSearch] = useState('')
  const [filterVenue, setFilterVenue] = useState('')
  const [filterYear, setFilterYear] = useState('')
  const [filterTag, setFilterTag] = useState('')


  const [videos, setVideos] = useState([])
  const [photos, setPhotos] = useState([])
  const [books, setBooks] = useState([])
  const [venues, setVenues] = useState([])
  const [videoCategories, setVideoCategories] = useState([])
  const [videoTags, setVideoTags] = useState([])
  
  const nocoCache = useRef(null)
  const nocoLoading = useRef(null)


  // const fetchPhotos = async () => {
  //   try {
  //     const records = await base('photos').select().all();
  //     const results = records
  //       .filter(one => one.get('active'))
  //       .filter(one => one.get('image'))
  //       .map(one => ({
  //         '_id': one.id,
  //         'id': one.get('id'),
  //         'order': one.get('order'),
  //         'image': one.get('image') ? one.get('image')[0] : {},
  //       }))
  //       .sort((a, b) => b.order - a.order)

  //     console.log('results = ', results);
  //     if (typeof results === 'object') setPhotos(results)
  //   } catch (error) {
  //     console.error('Error fetching records:', error);
  //   }
  // };

  // const fetchPhotos = async () => {
  //   try {
  //     // Fetch photos from Cloudinary with the updated cloud name
  //     const response = await fetch('https://api.cloudinary.com/v1_1/psdg/resources/image'); // Updated cloud name
  //     const records = await response.json(); // Assuming the response is in JSON format

  //     const results = records.resources
  //       .filter(one => one.active) // Adjust this based on your Cloudinary data structure
  //       .map(one => ({
  //         '_id': one.public_id,
  //         'id': one.public_id,
  //         'order': one.created_at,
  //         'image': one.secure_url ? one.secure_url : {},
  //       }))
  //       .sort((a, b) => b.order - a.order);

  //     console.log('results = ', results);
  //     if (Array.isArray(results)) setPhotos(results);
  //   } catch (error) {
  //     console.error('Error fetching records:', error);
  //   }
  // };


  const fetchPhotos = () => {
    try {
      // Map over the resources array to create an array of photo objects
      const photos = gallery.map(resource => ({
        _id: resource.asset_id, // Using public_id as _id
        id: resource.public_id,   // Using public_id as id
        image: resource.url, // Using secure_url for image
        width: resource.width,
        height: resource.height,
        display_name: resource.display_name,
      }));

      // Set the photos in your state (assuming you have a state setter)
      setPhotos(photos); // Assuming setPhotos is a state setter for your photos
    } catch (error) {
      console.error('Error processing photos:', error);
    }
  };

  const applyCollections = (collections, lang) => {
    const next = applyLanguage(collections, lang);
    setVideoCategories(next.videoCategories);
    setVenues(next.venues);
    setVideos(next.videos);
    setBooks(next.books);
    setReady(true);
  };

  const loadStaticCollections = async () => {
    const [categoriesResponse, venuesResponse, videosResponse, booksResponse] = await Promise.all([
      fetch('/data/videoCategories.json'),
      fetch('/data/venues.json'),
      fetch('/data/videos.json'),
      fetch('/data/books.json'),
    ]);

    const [staticCategories, staticVenues, staticVideos, staticBooks] = await Promise.all([
      categoriesResponse.ok ? categoriesResponse.json() : [],
      venuesResponse.ok ? venuesResponse.json() : [],
      videosResponse.ok ? videosResponse.json() : [],
      booksResponse.ok ? booksResponse.json() : [],
    ]);

    const toImage = (value) => {
      if (!value) return { url: '', width: 0, height: 0, thumbnails: { large: { url: '', width: 0, height: 0 } } };
      if (typeof value === 'string') {
        return { url: value, thumbnails: { large: { url: value } } };
      }
      if (value.thumbnails?.large) return value;
      const url = value.url || '';
      return { ...value, url, thumbnails: { large: { url, width: value.width, height: value.height } } };
    };

    return {
      videos: (staticVideos || [])
        .filter((one) => one.youtubeId)
        .map((one) => ({
          ...one,
          language: Array.isArray(one.language) ? one.language : ['en', 'ru'],
          tags: one.tags || [],
          categories: one.categories || [],
          venue_id: one.venue_id || [],
          venue_title: Array.isArray(one.venue_title) ? (one.venue_title[0] || '') : (one.venue_title || ''),
          venue_sub_title: Array.isArray(one.venue_sub_title) ? (one.venue_sub_title[0] || '') : (one.venue_sub_title || ''),
        })),
      videoCategories: (staticCategories || []).map((one) => ({
        ...one,
        videos: one.videos || [],
      })),
      venues: (staticVenues || []).map((one) => ({
        ...one,
        videos: one.videos || [],
        videoLanguages: one.videoLanguages || [],
      })),
      books: (staticBooks || []).map((one) => ({
        ...one,
        'image-en': toImage(one['image-en']),
        'image-ru': toImage(one['image-ru']),
      })),
    };
  };

  useEffect(() => {
    let cancelled = false;
    fetchPhotos();

    if (!nocoLoading.current) {
      nocoLoading.current = retryWithBackoff(() => loadNocoCollections())
        .then((collections) => {
          console.log('Loaded collections from NocoDB');
          return collections;
        })
        .catch(async (apiError) => {
          console.warn('Could not load from NocoDB, falling back to static JSON:', apiError);
          return loadStaticCollections();
        })
        .then((collections) => {
          nocoCache.current = collections;
          return collections;
        });
    }

    nocoLoading.current
      .then((collections) => {
        if (!cancelled && collections) {
          applyCollections(collections, language);
        }
      })
      .catch((error) => {
        console.error('Error fetching collections:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [language]);

  // Extract unique tags from videos and set them to videoTags
  useEffect(() => {
    if (videos && videos.length > 0) {
      const allTags = videos
        .flatMap(video => video.tags || [])
        .filter(tag => tag && tag.trim() !== '');
      
      const uniqueTags = [...new Set(allTags)];
      setVideoTags(uniqueTags);
      console.log('videoTags extracted:', uniqueTags);
    }
  }, [videos]);




  const getVideoById = (_id) => {
    let vid = videos.filter(one => one._id === _id)[0];
    if (vid) {
      vid['title'] = vid[`title-${language}`];
    } else {
      vid = {}
    }
    return vid;
  }

  const getVenueById = (id) => {
    let ven = venues.filter(one => one.id === id)[0];
    if (ven) {
      ven = ven;
    } else {
      ven = {}
    }
    return ven;
  }


  const filterLang = (arr) => {
    return arr.filter(one => one.language.includes(language))
  }

  const filterContent = (arr, { fv = filterVenue, fy = filterYear, ft = filterTag, s = search } = {}) => {
    let a = arr
    if (fv) {
      a = a.filter(one => one.venue_id.includes(fv))
        .sort((a, b) => b.order - a.order)
    }
    if (fy) {
      a = a.filter(one => one.date.split('-')[0] === fy)
        .sort((a, b) => b.order - a.order)
    }
    if (s) {
      a = a.filter(one => {
        let a = one.title.toLowerCase().search(typeof s === 'string'
          ? s.toLowerCase()
          : '')
        return a > -1
      })
        .sort((a, b) => b.order - a.order)
    }
    if (ft) {
      a = a.filter(one => one.tags.includes(ft))
        .sort((a, b) => b.order - a.order)
    }

    return a
  }


  // useEffect(() => {
  //   if (!filterTag) {
  //     const v = filterContent(videos, { ft: filterTag })
  //   }
  // }, [filterTag]);


  return (<>
    <Context.Provider
      value={{
        state: {
          language,
          ready,
          filterVenue,
          filterYear,
          filterTag,
          search,
          view,
        },
        videoCategories,
        videos,
        photos,
        books,
        venues,
        videoTags,
        x: {
          filterContent,
          filterLang,
          getVideoById,
          getVenueById,
          setPhotos,
          setBooks,
          setLanguage,
          setFilterVenue,
          setFilterYear,
          setFilterTag,
          setSearch,
          setView,
        },
      }}
    >
      {children}
    </Context.Provider>
  </>
  )

}

const Consumer = Context.Consumer;

export { Context, Provider, Consumer };
